import { getBusinessConf } from '../core/config';

const CANCELLED = 'CANCELLED';
const ERROR = 'ERROR';
const PAID = 'PAID';
const PAID_EXTERNALLY = 'PAID_EXTERNALLY';
const PENDING_AUTHENTICATION = 'PENDING_AUTHENTICATION';
const PENDING_MANUAL_PAYMENT = 'PENDING_MANUAL_PAYMENT';
const REFUND_IN_PROGRESS = 'REFUND_IN_PROGRESS';
const REFUNDED = 'REFUNDED';
const REFUSED = 'REFUSED';

/**
 * @typedef {Object} InvoiceActions
 * @property {'cancelInvoice'} CANCEL_INVOICE - Action to **cancel** an invoice.
 * @property {'creditNote'} CREDIT_NOTE - Action to **download** a credit note.
 * @property {'externalPayment'} EXTERNAL_PAYMENT - Action that set the invoice status to `PAID` and set `isPaidExternally` to true.
 * @property {'invoice'} INVOICE - Action to **download** an invoice.
 * @property {'payNow'} PAY_NOW - Action to **manually** execute a payment related to an invoice.
 * @property {'pay'} PAY - Action to **pay** (set the invoice status to `PENDING`).
 * @property {'refund'} REFUND - Action to refund.
 * @property {'refundNote'} REFUND_NOTE - Action to **download** a refund note.
 * @property {'tripId'} TRIP_ID - Link to redirect to the trip details.
 */

/**
 * @type {InvoiceActions}
 */
export const invoiceActions = {
  CANCEL_INVOICE: 'cancelInvoice',
  CREDIT_NOTE: 'creditNote',
  EXTERNAL_PAYMENT: 'externalPayment',
  INVOICE: 'invoice',
  PAY_NOW: 'payNow',
  PAY: 'pay',
  REFUND: 'refund',
  REFUND_NOTE: 'refundNote',
  TRIP_ID: 'tripId',
};

const {
  CANCEL_INVOICE,
  CREDIT_NOTE,
  EXTERNAL_PAYMENT,
  INVOICE,
  PAY,
  PAY_NOW,
  REFUND,
  REFUND_NOTE,
  TRIP_ID,
} = invoiceActions;

/**
 * Invoice status type
 * @typedef {('CANCELLED' | 'ERROR' | 'MOP_MISSING' | 'PAID' | 'PAID_EXTERNALLY' | 'PENDING_AUTHENTICATION' | 'PENDING_EXTERNAL_PAYMENT' | 'PENDING_MANUAL_PAYMENT' | 'PENDING' | 'REFUNDED' | 'REFUSED')} InvoiceStatus

/**
 * Check if the currentStatus is allowed for the action
 * @param {InvoiceStatus} currentStatus
 * @param {Array<InvoiceStatus>} allowedStatus
 * @returns {boolean}
 */
const checkAllowedStatus = (currentStatus, allowedStatus) => allowedStatus.includes(currentStatus);

/**
 * Get the displayed actions for the invoice
 * @param {Object} invoice
 * @param {InvoiceStatus} invoice.invoiceStatus
 * @param {('cancelInvoice' | 'creditNote' | 'externalPayment' | 'invoice' | 'payNow' | 'pay' | 'refund' | 'tripId')} invoice.action
 * @param {boolean} [invoice.paymentInProgress]
 * @param {boolean} [invoice.refundInProgress]
 * @param {string} [invoice.invoiceId]
 * @param {boolean} [invoice.customCheck=true] - Have priority over the default check
 */
export const getInvoiceDisplayedActions = ({
  action,
  customCheck = true,
  invoiceId,
  invoiceStatus,
  paymentInProgress,
  refundInProgress,
}) => {
  let isDisplayed = false;

  const actionInProgress = paymentInProgress || refundInProgress;

  const role = localStorage.getItem('role');
  const isAdmin = role === 'admin';

  switch (action) {
    case CANCEL_INVOICE:
      isDisplayed =
        !actionInProgress &&
        !checkAllowedStatus(invoiceStatus, [PAID, PAID_EXTERNALLY, CANCELLED, REFUNDED]) &&
        isAdmin;
      break;
    case CREDIT_NOTE:
      isDisplayed = invoiceId && checkAllowedStatus(invoiceStatus, [CANCELLED]);
      break;
    case EXTERNAL_PAYMENT:
      isDisplayed =
        !actionInProgress &&
        !checkAllowedStatus(invoiceStatus, [CANCELLED, PAID, PAID_EXTERNALLY, PENDING_AUTHENTICATION, REFUNDED]) &&
        isAdmin;
      break;
    case INVOICE:
      isDisplayed = invoiceId && !checkAllowedStatus(invoiceStatus, [CANCELLED]) && isAdmin;
      break;
    case PAY_NOW:
      isDisplayed = !actionInProgress && checkAllowedStatus(invoiceStatus, [PENDING_MANUAL_PAYMENT]);
      break;
    case PAY:
      isDisplayed = !actionInProgress && checkAllowedStatus(invoiceStatus, [ERROR, REFUSED]);
      break;
    case REFUND:
      isDisplayed = invoiceId && checkAllowedStatus(invoiceStatus, [PAID, REFUNDED, REFUND_IN_PROGRESS]) && isAdmin;
      break;
    case REFUND_NOTE:
      isDisplayed = invoiceId && checkAllowedStatus(invoiceStatus, [REFUNDED]) && isAdmin;
      break;
    case TRIP_ID:
      isDisplayed = Boolean(invoiceId);
      break;
    default:
      break;
  }

  return customCheck && isDisplayed;
};

/**
 * Returns an alternate status based on the provided flags.
 *
 * @param {Object} params - The parameters object.
 * @param {string} params.mainStatus - The main status of the invoice.
 * @param {boolean} params.refundInProgress - Indicates if a refund is in progress.
 * @param {boolean} params.paymentInProgress - Indicates if a payment is in progress.
 * @param {boolean} params.isPaidExternally - Indicates if the invoice is paid externally.
 * @returns {string} The alternate status based on the provided flags.
 */
export const getAlternateStatus = ({ mainStatus, refundInProgress, paymentInProgress, isPaidExternally }) => {
  if (refundInProgress) {
    return 'REFUND_IN_PROGRESS';
  }
  if (paymentInProgress) {
    return 'PAYMENT_IN_PROGRESS';
  }
  if (isPaidExternally) {
    return 'PAID_EXTERNALLY';
  }
  return mainStatus;
};

/**
 * Checks if unit invoices are enabled for the current fleet.
 *
 * This function retrieves the business configuration and the current fleet ID from local storage.
 * It then checks if the current fleet ID is included in the list of allowed fleets for unit invoices.
 *
 * @returns {boolean} - Returns true if unit invoices are enabled for the current fleet, otherwise false.
 */
export const getIsUnitInvoicesEnabled = () => {
  const conf = getBusinessConf();
  const fleetId = localStorage.getItem('fleetID');

  const allowedFleets = conf.config && conf.config.businessUnitInvoicesVisible;

  if (Array.isArray(allowedFleets) && allowedFleets.includes(fleetId)) return true;

  return false;
};
